import React from 'react';
import { logError } from '../../utils/errorHandler';

/**
 * Main Error Boundary component
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      errorId: Date.now().toString()
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error
    const errorId = this.state.errorId;
    
    logError(error, {
      errorInfo,
      errorId,
      component: this.props.name || 'Unknown',
      userId: this.props.userId,
      timestamp: new Date().toISOString()
    });

    this.setState({
      error,
      errorInfo
    });

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo, errorId);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });

    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback(
          this.state.error,
          this.state.errorInfo,
          this.handleRetry,
          this.state.errorId
        );
      }

      // Default fallback UI
      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <div className="error-boundary-icon">⚠️</div>
            <h2 className="error-boundary-title">
              {this.props.title || 'Something went wrong'}
            </h2>
            <p className="error-boundary-message">
              {this.props.message || 'An unexpected error occurred. Please try again.'}
            </p>
            
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-boundary-details">
                <summary>Error Details (Development)</summary>
                <pre className="error-boundary-stack">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
            
            <div className="error-boundary-actions">
              <button 
                className="btn btn-primary"
                onClick={this.handleRetry}
              >
                Try Again
              </button>
              <button 
                className="btn btn-secondary"
                onClick={this.handleReload}
              >
                Reload Page
              </button>
            </div>
            
            {this.state.errorId && (
              <p className="error-boundary-id">
                Error ID: {this.state.errorId}
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Specialized Error Boundary for trading operations
 */
export class TradingErrorBoundary extends React.Component {
  render() {
    return (
      <ErrorBoundary
        name="TradingErrorBoundary"
        title="Trading Error"
        message="An error occurred during trading operations. Your funds are safe."
        fallback={(error, errorInfo, retry, errorId) => (
          <div className="trading-error-boundary">
            <div className="trading-error-content">
              <div className="trading-error-icon">🛡️</div>
              <h3>Trading Operation Failed</h3>
              <p>Don't worry - your funds are safe. The trading operation encountered an error.</p>
              
              <div className="trading-error-actions">
                <button className="btn btn-primary" onClick={retry}>
                  Retry Trading
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => window.location.href = '/dashboard'}
                >
                  Go to Dashboard
                </button>
              </div>
              
              <p className="error-id">Error ID: {errorId}</p>
            </div>
          </div>
        )}
        {...this.props}
      />
    );
  }
}

/**
 * Specialized Error Boundary for wallet operations
 */
export class WalletErrorBoundary extends React.Component {
  render() {
    return (
      <ErrorBoundary
        name="WalletErrorBoundary"
        title="Wallet Error"
        message="An error occurred with wallet operations."
        fallback={(error, errorInfo, retry, errorId) => (
          <div className="wallet-error-boundary">
            <div className="wallet-error-content">
              <div className="wallet-error-icon">👛</div>
              <h3>Wallet Operation Failed</h3>
              <p>There was an issue with your wallet connection or operation.</p>
              
              <div className="wallet-error-suggestions">
                <h4>Try these steps:</h4>
                <ul>
                  <li>Check if your wallet is unlocked</li>
                  <li>Refresh your wallet connection</li>
                  <li>Make sure you have sufficient balance</li>
                  <li>Try connecting a different wallet</li>
                </ul>
              </div>
              
              <div className="wallet-error-actions">
                <button className="btn btn-primary" onClick={retry}>
                  Retry
                </button>
                <button 
                  className="btn btn-secondary"
                  onClick={() => window.location.href = '/wallet'}
                >
                  Wallet Settings
                </button>
              </div>
              
              <p className="error-id">Error ID: {errorId}</p>
            </div>
          </div>
        )}
        {...this.props}
      />
    );
  }
}

/**
 * HOC for wrapping components with error boundary
 */
export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
  const WrappedComponent = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );
  
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

/**
 * Hook for error boundary context (for functional components)
 */
export const useErrorHandler = () => {
  const [error, setError] = React.useState(null);
  
  const resetError = React.useCallback(() => {
    setError(null);
  }, []);
  
  const handleError = React.useCallback((error) => {
    logError(error, { source: 'useErrorHandler' });
    setError(error);
  }, []);
  
  // Throw error to be caught by error boundary
  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);
  
  return { handleError, resetError };
};

export default ErrorBoundary;